<template>
    <article class="article-content">
        <section class="content-section">
            <h2>{{ sections?.intro?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.intro?.content)"
                :key="'intro-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section">
            <h2>{{ sections?.rust_footprint?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.rust_footprint?.content)"
                :key="'rust-footprint-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <div class="stats-table-wrapper">
                <table class="stats-table">
                    <caption>
                        {{
                            sections?.rust_footprint?.table_caption
                        }}
                    </caption>
                    <thead>
                        <tr>
                            <th>{{ sections?.rust_footprint?.table_headers?.image }}</th>
                            <th>{{ sections?.rust_footprint?.table_headers?.image_size }}</th>
                            <th>{{ sections?.rust_footprint?.table_headers?.idle_ram }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>core (API)</td>
                            <td>160 MB</td>
                            <td>~38 MB</td>
                        </tr>
                        <tr>
                            <td>worker</td>
                            <td>130 MB</td>
                            <td>~3 MB</td>
                        </tr>
                        <tr>
                            <td>maintenance</td>
                            <td>120 MB</td>
                            <td>~3 MB</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>

        <section class="content-section">
            <h2>{{ sections?.lean_code?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.lean_code?.content)"
                :key="'lean-code-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section">
            <h2>{{ sections?.open_source?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.open_source?.content)"
                :key="'open-source-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section">
            <h2>{{ sections?.rdatacore_integration?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(
                    sections?.rdatacore_integration?.content
                )"
                :key="'rdatacore-integration-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure article-figure--narrow">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/efficient-by-nature/sloth_metabolism-400w.webp   400w,
                            /images/blog/efficient-by-nature/sloth_metabolism-800w.webp   800w,
                            /images/blog/efficient-by-nature/sloth_metabolism-1200w.webp 1200w,
                            /images/blog/efficient-by-nature/sloth_metabolism-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 70vw, (max-width: 1200px) 56vw, 560px"
                    />
                    <img
                        src="/images/blog/efficient-by-nature/sloth_metabolism.png"
                        alt="Sloth metabolic rate compared with a similarly sized cat"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.sloth_metabolism }}</figcaption>
            </figure>
        </section>

        <section class="content-section teaser-section">
            <h2>{{ sections?.teaser?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.teaser?.content)"
                :key="'teaser-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <div class="coming-soon-badge">
                <SmartIcon
                    icon="clock"
                    :size="18"
                />
                {{ t('blog.coming_soon') }}: Rust Service Architecture Deep Dive
            </div>
        </section>
    </article>
</template>

<script setup lang="ts">
    import {
        useBlogFormatter,
        type PostSections,
        type PostCaptions,
    } from '@/composables/useBlogFormatter'
    import { useTranslations } from '@/composables/useTranslations'
    import SmartIcon from '@/components/common/SmartIcon.vue'

    defineProps<{
        sections?: PostSections
        captions?: PostCaptions
    }>()

    const { t } = useTranslations()
    const { splitParagraphs, formatContent } = useBlogFormatter()
</script>

<style scoped>
    .stats-table-wrapper {
        overflow-x: auto;
        margin: 1.5rem 0;
    }

    .stats-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 0.9rem;
        color: rgb(var(--v-theme-on-surface));
    }

    .stats-table caption {
        caption-side: bottom;
        padding: 0.5rem 0;
        font-size: 0.8rem;
        color: rgb(var(--v-theme-on-surface-variant));
        text-align: left;
    }

    .stats-table th,
    .stats-table td {
        padding: 0.6rem 1rem;
        text-align: left;
        border-bottom: 1px solid rgba(var(--v-theme-on-surface-variant), 0.2);
    }

    .stats-table th {
        font-weight: 600;
        background: rgba(var(--v-theme-surface-variant), 0.5);
        color: rgb(var(--v-theme-on-surface));
    }

    .stats-table tbody tr:hover {
        background: rgba(var(--v-theme-surface-variant), 0.3);
    }
</style>
